"use client";

import { useLanguage } from "@/app/context/LanguageContext";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import Image from "next/image";
import { useState } from "react";
import {
  Sparkles, Landmark, Star, Sword, Flame, Heart, Flower2, User,
  Shield, Sprout, Scale, Users, Music, Moon, Utensils, Building2,
  Zap, Lamp, Wheat, Wand2, Flower, Lightbulb, Bird, Phone,
  ChevronDown, MapPin, Clock, CalendarDays, Ruler, Layers,
  ArrowRight, Check
} from "lucide-react";

const emojiToIcon: { [key: string]: React.ReactNode } = {
  "🏛️": <Building2 className="h-5 w-5" />,
  "🌟": <Star className="h-5 w-5" />,
  "🔱": <Wand2 className="h-5 w-5" />,
  "🪔": <Lamp className="h-5 w-5" />,
  "🌺": <Flower className="h-5 w-5" />,
  "🌸": <Flower2 className="h-5 w-5" />,
  "🌙": <Moon className="h-5 w-5" />,
  "🌿": <Sprout className="h-5 w-5" />,
  "🙏": <Heart className="h-5 w-5" />,
  "🔥": <Flame className="h-5 w-5" />,
  "🥥": <Wand2 className="h-5 w-5" />,
  "🎺": <Music className="h-5 w-5" />,
  "🌾": <Wheat className="h-5 w-5" />,
  "🕯️": <Lightbulb className="h-5 w-5" />,
  "🍛": <Utensils className="h-5 w-5" />,
  "🛡️": <Shield className="h-5 w-5" />,
  "⚖️": <Scale className="h-5 w-5" />,
  "👨‍👩‍👧‍👦": <Users className="h-5 w-5" />,
  "🎋": <Sprout className="h-5 w-5" />,
  "⚔️": <Sword className="h-5 w-5" />,
  "🐘": <Bird className="h-5 w-5" />,
  "👤": <User className="h-5 w-5" />,
  "📞": <Phone className="h-5 w-5" />,
};

const content = {
  en: {
    badge: "Our Sacred Story",
    title: "About the Temple",
    subtitle:
      "A divine sanctuary rooted in devotion, tradition, and the protecting grace of Karuppanasamy",

    history: {
      badge: "Temple History",
      title: "Born from Devotion",
      subtitle: "2014 -  The Year of Divine Inauguration",
      body1:
        "The 18m Padi Karuppanasamy Temple was consecrated and inaugurated in the year 2014, standing as a testament to the unwavering faith of the Sukkampatti community in Kadavoor Thaluk, Karur District, Tamil Nadu.",
      body2:
        "Over the past 12 glorious years, the temple has grown from a sacred vision into a thriving spiritual destination. The presiding deity -  a magnificent 22-foot tall Karuppanasamy statue -  watches over devotees with divine grace, drawing pilgrims from surrounding villages and beyond.",
      body3:
        "The temple was established under the stewardship of Trustee Kalimuthu (son of Vengatajalam and Palaniyamaal), whose family has devoted their lives to maintaining this sacred space. With the support of two dedicated male staff members, the temple operates with devotion every single day of the year.",
      milestones: [
        { year: "2014", label: "Temple Inaugurated", icon: "🏛️" },
        { year: "12", label: "Years of Service", icon: "🌟" },
        { year: "22ft", label: "Statue Height", icon: "🔱" },
        { year: "6+", label: "Deities Worshipped", icon: "🪔" },
      ],
    },

    karuppasamy: {
      badge: "Lord Karuppanasamy",
      title: "The Divine Protector",
      subtitle: "Guardian deity of the land, fierce yet benevolent",
      body1:
        "Karuppanasamy -  also known as Karuppar, Karuppan, or Karuppu Sami -  is one of the most revered guardian deities of Tamil Nadu. Depicted as a dark-complexioned warrior, He is the fierce protector of villages, fields, and communities.",
      body2:
        "Traditionally stationed at the outskirts of villages, Karuppanasamy is believed to guard against evil spirits, calamities, and injustice. Devotees approach Him with absolute faith, knowing that He answers prayers with swift and direct justice.",
      body3:
        "At this temple, Karuppanasamy stands 22 feet tall -  an awe-inspiring presence that conveys both power and compassion. He is worshipped alongside Kaali Amman, Bairavar, Murugan, Vinayagar, and the Navagraha, forming a complete divine assembly.",
      deities: [
        { name: "Karuppanasamy", role: "Presiding Deity", icon: "🔱" },
        { name: "Kaali Amman", role: "Divine Mother", icon: "🌺" },
        { name: "Bairavar", role: "Guardian of Time", icon: "⚔️" },
        { name: "Murugan", role: "God of War & Wisdom", icon: "🌸" },
        { name: "Vinayagar", role: "Remover of Obstacles", icon: "🐘" },
        { name: "Navagram", role: "Nine Celestial Bodies", icon: "🌙" },
      ],
    },

    importance: {
      badge: "Temple Importance",
      title: "Why This Temple Matters",
      subtitle: "A spiritual lighthouse for the Karur region",
      points: [
        {
          icon: "🛡️",
          title: "Community Protection",
          desc: "The temple serves as the spiritual guardian of Sukkampatti and surrounding villages in Kadavoor Thaluk. Devotees believe Karuppanasamy actively protects the community from harm.",
        },
        {
          icon: "🌿",
          title: "Agricultural Blessings",
          desc: "Farmers from across the region visit the temple seeking blessings for their crops. Karuppanasamy is traditionally the deity of fields, rain, and harvest.",
        },
        {
          icon: "⚖️",
          title: "Justice & Truth",
          desc: "The deity is widely invoked for justice in disputes. Devotees take sacred oaths before Karuppanasamy, trusting in His unwavering commitment to truth.",
        },
        {
          icon: "👨‍👩‍👧‍👦",
          title: "Family Welfare",
          desc: "Families offer prayers for health, prosperity, and protection. The temple welcomes all -  cutting across barriers -  as Karuppanasamy belongs to everyone.",
        },
        {
          icon: "🎋",
          title: "Cultural Heritage",
          desc: "The temple is a living centre of Tamil folk culture, preserving ancient traditions, oral histories, and ritual practices that connect generations.",
        },
        {
          icon: "🙏",
          title: "Open Every Day",
          desc: "Unlike many temples, this sacred space remains open all 7 days of the week -  morning 4 AM to 12 PM and evening 4 PM to 7 PM -  welcoming every seeker.",
        },
      ],
    },

    traditions: {
      badge: "Village Traditions",
      title: "Rooted in Folk Heritage",
      subtitle: "Ancient customs kept alive by generations of devotion",
      items: [
        {
          icon: "🔥",
          title: "Fire Walking (Thimithi)",
          desc: "On special festival days, devoted pilgrims walk on burning coals as an act of faith and fulfilment of vows to Karuppanasamy.",
        },
        {
          icon: "🥥",
          title: "Coconut Breaking Ritual",
          desc: "Devotees break coconuts before the deity as a symbol of surrendering the ego (represented by the hard outer shell) before the divine.",
        },
        {
          icon: "🎺",
          title: "Nadaswaram & Drums",
          desc: "Traditional music -  nadaswaram, parai, and urumi melam -  accompanies all major rituals, filling the temple precincts with sacred sound.",
        },
        {
          icon: "🌾",
          title: "Harvest Thanksgiving",
          desc: "After each harvest season, farmers bring the first yield to Karuppanasamy as an offering of gratitude before it is consumed by the family.",
        },
        {
          icon: "🕯️",
          title: "Oil Lamp Processions",
          desc: "On Amavasai and Pavarnamni nights, devotees carry oil lamps in procession around the temple, a practice believed to dispel darkness and negativity.",
        },
        {
          icon: "🍛",
          title: "Annadhanam (Community Feast)",
          desc: "On major festival days, free meals are distributed to all who arrive -  regardless of background -  embodying the principle that no one leaves the temple hungry.",
        },
      ],
    },

    architecture: {
      badge: "Temple Architecture",
      title: "Sacred Space, Divine Design",
      subtitle: "Built with devotion, standing with majesty",
      body1:
        "The 18m Padi Karuppanasamy Temple is designed in the Dravidian architectural tradition, the classical style of Tamil Nadu temple building. The temple complex is centred around the towering 22-foot Karuppanasamy statue -  a dominant landmark visible from afar.",
      body2:
        "The sanctum sanctorum (garbhagriha) houses the primary deity, Karuppanasamy, flanked by Kaali Amman and Bairavar. Adjacent shrines are dedicated to Murugan, Vinayagar, and the Navagraha, creating a harmonious divine assembly within the compound.",
      body3:
        "The temple entrance features traditional motifs and sculptures that narrate the glory of Karuppanasamy. Devotees pass through a ceremonial gateway before approaching the main shrine, following the ritual progression prescribed by ancient Agama texts.",
      features: [
        { label: "Architectural Style", value: "Dravidian", icon: <Building2 className="h-4 w-4 text-amber-500" /> },
        { label: "Primary Deity Height", value: "22 Feet", icon: <Ruler className="h-4 w-4 text-amber-500" /> },
        { label: "Total Deities", value: "6 + Navagraha", icon: <Layers className="h-4 w-4 text-amber-500" /> },
        { label: "Established", value: "2014", icon: <CalendarDays className="h-4 w-4 text-amber-500" /> },
        { label: "Location", value: "Sukkampatti, Karur", icon: <MapPin className="h-4 w-4 text-amber-500" /> },
        { label: "Staff", value: "2 Dedicated Servants", icon: <Users className="h-4 w-4 text-amber-500" /> },
      ],
    },

    contact: {
      trustee: "Trustee",
      name: "Kalimuthu",
      father: "Father: Vengatajalam",
      mother: "Mother: Palaniyamaal",
      brother: "Brother: Krishnamoorthy",
      address: "Kadavoor Thaluk, Kaniyalampatti Road, Sukkampatti, Karur",
      phones: ["9597255401", "9361986095"],
    },
  },

  ta: {
    badge: "எங்கள் புனித வரலாறு",
    title: "கோவிலைப் பற்றி",
    subtitle:
      "கருப்பண்ணசாமியின் பாதுகாப்பு அருளில் பக்தி, மரபு மற்றும் நம்பிக்கையில் வேரூன்றிய புனித தலம்",
    history: {
      badge: "கோவில் வரலாறு",
      title: "பக்தியிலிருந்து பிறந்தது",
      subtitle: "2014 -  திருவிழா திறப்பு ஆண்டு",
      body1: "18ம் படி கருப்பண்ணசாமி கோவில் 2014 ஆம் ஆண்டு கடவூர் தாலுகாவில் உள்ள சுக்கம்பட்டி சமுதாயத்தின் அசைக்கவியலா நம்பிக்கையின் சான்றாக திருக்கும்பாபிஷேகம் செய்யப்பட்டு திறக்கப்பட்டது.",
      body2: "கடந்த 12 பெருமை மிக்க ஆண்டுகளில், கோவில் ஒரு புனித தரிசனத்திலிருந்து வளர்ந்த ஆன்மீக தலமாக மாறியுள்ளது. 22 அடி உயரமுள்ள கருப்பண்ணசாமி சிலை தெய்வீக அருளுடன் பக்தர்களை வழிநடத்துகிறது.",
      body3: "கோவில் நம்பகர் களிமுத்து (வெங்கடாசலம் -  பழனியம்மாள் புதல்வர்) தலைமையில் நிறுவப்பட்டது.",
      milestones: [
        { year: "2014", label: "கோவில் திறப்பு", icon: "🏛️" },
        { year: "12", label: "ஆண்டுகள் சேவை", icon: "🌟" },
        { year: "22ft", label: "சிலை உயரம்", icon: "🔱" },
        { year: "6+", label: "தெய்வங்கள்", icon: "🪔" },
      ],
    },
    karuppasamy: {
      badge: "கருப்பண்ணசாமி",
      title: "காவல் தெய்வம்",
      subtitle: "நாட்டின் காவல் தெய்வம், கோரமானவர் ஆனால் அருளானவர்",
      body1: "கருப்பண்ணசாமி -  கருப்பர், கருப்பன் அல்லது கருப்பு சாமி என்றும் அழைக்கப்படும் -  தமிழ்நாட்டின் மிகவும் போற்றப்படும் காவல் தெய்வங்களில் ஒருவர்.",
      body2: "பாரம்பரியமாக கிராம எல்லையில் அமர்ந்திருக்கும் கருப்பண்ணசாமி தீய ஆவிகள், இன்னல்கள் மற்றும் அநீதிக்கு எதிராக பாதுகாக்கிறார் என்று நம்பப்படுகிறது.",
      body3: "இந்த கோவிலில் கருப்பண்ணசாமி 22 அடி உயரத்தில் நிற்கிறார் -  சக்தி மற்றும் இரக்கம் இரண்டையும் தெரிவிக்கும் அசாத்திய நிலை.",
      deities: [
        { name: "கருப்பண்ணசாமி", role: "முதன்மை தெய்வம்", icon: "🔱" },
        { name: "காளி அம்மன்", role: "தெய்வீக அன்னை", icon: "🌺" },
        { name: "பைரவர்", role: "காலத்தின் காவலர்", icon: "⚔️" },
        { name: "முருகன்", role: "போர் & ஞானத்தின் கடவுள்", icon: "🌸" },
        { name: "விநாயகர்", role: "தடைகளை நீக்குபவர்", icon: "🐘" },
        { name: "நவகிரகம்", role: "ஒன்பது வான் உடல்கள்", icon: "🌙" },
      ],
    },
    importance: {
      badge: "கோவிலின் முக்கியத்துவம்",
      title: "இந்த கோவில் ஏன் முக்கியமானது",
      subtitle: "கரூர் பகுதிக்கான ஆன்மீக கலங்கரை விளக்கம்",
      points: [
        { icon: "🛡️", title: "சமுதாய பாதுகாப்பு", desc: "கோவில் கடவூர் தாலுகாவில் உள்ள சுக்கம்பட்டி மற்றும் சுற்றுவட்டார கிராமங்களின் ஆன்மீக காவலராக செயல்படுகிறது." },
        { icon: "🌿", title: "விவசாய ஆசீர்வாதங்கள்", desc: "பகுதி முழுவதும் உள்ள விவசாயிகள் தங்கள் பயிருக்கு ஆசீர்வாதம் தேடி கோவிலுக்கு வருகிறார்கள்." },
        { icon: "⚖️", title: "நீதி & உண்மை", desc: "தெய்வம் சர்ச்சைகளில் நீதிக்காக பரவலாக வேண்டப்படுகிறது. பக்தர்கள் கருப்பண்ணசாமி முன் புனித சத்தியங்கள் எடுக்கின்றனர்." },
        { icon: "👨‍👩‍👧‍👦", title: "குடும்ப நலன்", desc: "குடும்பங்கள் ஆரோக்கியம், செழிப்பு மற்றும் பாதுகாப்புக்காக வழிபடுகின்றன. கோவில் அனைவரையும் வரவேற்கிறது." },
        { icon: "🎋", title: "கலாசார பாரம்பரியம்", desc: "கோவில் தமிழ் நாட்டுப்புற கலாசாரத்தின் உயிரோட்டமான மையமாக உள்ளது." },
        { icon: "🙏", title: "தினசரி திறப்பு", desc: "வாரம் 7 நாட்களும் திறந்திருக்கும் -  காலை 4 AM முதல் 12 PM வரை மற்றும் மாலை 4 PM முதல் 7 PM வரை." },
      ],
    },
    traditions: {
      badge: "கிராம மரபுகள்",
      title: "நாட்டுப்புற பாரம்பரியத்தில் வேரூன்றியது",
      subtitle: "பக்தியின் தலைமுறைகளால் உயிரோட்டமுள்ள பழமையான பழக்கங்கள்",
      items: [
        { icon: "🔥", title: "தீ நடை (திமிதி)", desc: "சிறப்பு விழா நாட்களில், பக்தர்கள் கருப்பண்ணசாமிக்கு நேர்த்திக்கடன் நிறைவேற்றும் செயலாக எரியும் நிலக்கரியில் நடக்கின்றனர்." },
        { icon: "🥥", title: "தேங்காய் உடைக்கும் சடங்கு", desc: "பக்தர்கள் தெய்வத்தின் முன் தேங்காய் உடைக்கின்றனர் -  தெய்வீக அர்ப்பணிப்பின் அடையாளம்." },
        { icon: "🎺", title: "நாதஸ்வரம் & மேளம்", desc: "பாரம்பரிய இசை -  நாதஸ்வரம், பறை மற்றும் உரிமி மேளம் -  அனைத்து முக்கிய சடங்குகளிலும் புனித ஒலியுடன் கோவிலை நிரப்புகிறது." },
        { icon: "🌾", title: "அறுவடை நன்றி", desc: "ஒவ்வொரு அறுவடை பருவத்திற்கும் பிறகு, விவசாயிகள் கட்டாணி நிவேதனமாக கொண்டு வருகிறார்கள்." },
        { icon: "🕯️", title: "எண்ணெய் தீப ஊர்வலம்", desc: "அமாவாசை மற்றும் பவுர்ணமி இரவுகளில், பக்தர்கள் கோவிலை சுற்றி எண்ணெய் தீபங்களுடன் ஊர்வலம் செல்கின்றனர்." },
        { icon: "🍛", title: "அன்னதானம் (சமுதாய விருந்து)", desc: "பெரிய விழா நாட்களில், வருகை தரும் அனைவருக்கும் இலவச உணவு வழங்கப்படுகிறது." },
      ],
    },
    architecture: {
      badge: "கோவில் கட்டிடக்கலை",
      title: "புனித இடம், தெய்வீக வடிவமைப்பு",
      subtitle: "பக்தியுடன் கட்டப்பட்டது, மாட்சியுடன் நிற்கிறது",
      body1: "18ம் படி கருப்பண்ணசாமி கோவில் தமிழ்நாட்டு கோவில் கட்டுமானத்தின் கிளாசிக்கல் பாணியான திராவிட கட்டிட மரபில் வடிவமைக்கப்பட்டுள்ளது.",
      body2: "கர்ப்பகிரகம் முதன்மை தெய்வம் கருப்பண்ணசாமியை தாங்கி நிற்கிறது, காளி அம்மன் மற்றும் பைரவருடன்.",
      body3: "கோவில் நுழைவாயில் பாரம்பரிய உருவங்கள் மற்றும் சிற்பங்களை கொண்டிருக்கிறது.",
      features: [
        { label: "கட்டிடக்கலை பாணி", value: "திராவிடம்", icon: <Building2 className="h-4 w-4 text-amber-500" /> },
        { label: "முதன்மை சிலை உயரம்", value: "22 அடி", icon: <Ruler className="h-4 w-4 text-amber-500" /> },
        { label: "மொத்த தெய்வங்கள்", value: "6 + நவகிரகம்", icon: <Layers className="h-4 w-4 text-amber-500" /> },
        { label: "நிறுவப்பட்டது", value: "2014", icon: <CalendarDays className="h-4 w-4 text-amber-500" /> },
        { label: "இருப்பிடம்", value: "சுக்கம்பட்டி, கரூர்", icon: <MapPin className="h-4 w-4 text-amber-500" /> },
        { label: "ஊழியர்கள்", value: "2 அர்ப்பணிப்புள்ள சேவகர்கள்", icon: <Users className="h-4 w-4 text-amber-500" /> },
      ],
    },
    contact: {
      trustee: "நம்பகர்",
      name: "களிமுத்து",
      father: "தந்தை: வெங்கடாசலம்",
      mother: "தாய்: பழனியம்மாள்",
      brother: "சகோதரர்: கிருஷ்ணமூர்த்தி",
      address: "கடவூர் தாலுகா, கணியாலம்பட்டி ரோடு, சுக்கம்பட்டி, கரூர்",
      phones: ["9597255401", "9361986095"],
    },
  },
};

const images = [
  "/about/img1.jpg",
  "/about/img2.jpg",
  "/about/img3.jpg",
];

export default function AboutPage() {
  const { language } = useLanguage();
  const t = content[language as "en" | "ta"] ?? content.en;
  const [expandedTradition, setExpandedTradition] = useState<number | null>(null);

  return (
    <main className="bg-[#0a0804] text-neutral-100 min-h-screen selection:bg-amber-900 selection:text-amber-100 overflow-x-hidden">

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex flex-col items-center justify-center text-center px-4 overflow-hidden">
        {/* Layered background glow */}
        <div className="absolute inset-0 bg-gradient-to-b from-amber-950/40 via-[#0a0804] to-[#0a0804]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-600/40 to-transparent" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-amber-700/10 rounded-full blur-[160px] pointer-events-none" />
        <div className="absolute top-10 left-8 w-64 h-64 bg-orange-900/15 rounded-full blur-[80px] pointer-events-none" />
        <div className="absolute bottom-20 right-8 w-80 h-80 bg-amber-800/10 rounded-full blur-[100px] pointer-events-none" />

        {/* Decorative dots grid */}
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: 'radial-gradient(circle, #f59e0b 1px, transparent 1px)', backgroundSize: '40px 40px' }}
        />

        <div className="relative z-10 space-y-6 max-w-4xl mx-auto pt-20 pb-10">
          {/* Sacred ornament */}
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-600/60" />
            <div className="w-2 h-2 rounded-full bg-amber-500 shadow-lg shadow-amber-500/50" />
            <Sparkles className="h-4 w-4 text-amber-400" />
            <div className="w-2 h-2 rounded-full bg-amber-500 shadow-lg shadow-amber-500/50" />
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-600/60" />
          </div>

          <Badge
            variant="outline"
            className="text-amber-300 border-amber-500/40 bg-amber-950/40 backdrop-blur-md px-6 py-2 text-xs font-medium tracking-[0.3em] uppercase rounded-full"
          >
            {t.badge}
          </Badge>

          <h1 className="text-4xl sm:text-7xl lg:text-8xl font-bold leading-none tracking-tight px-2">
            <span className="bg-gradient-to-br from-amber-100 via-yellow-300 to-orange-500 bg-clip-text text-transparent drop-shadow-sm">
              {t.title}
            </span>
          </h1>
          Broadway

          <p className="text-base md:text-xl text-amber-100/40 font-light max-w-2xl mx-auto leading-relaxed">
            {t.subtitle}
          </p>

          {/* CTA scroll hint */}

        </div>

        {/* Hero image mosaic */}
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-3 max-w-5xl mx-auto px-4 w-full pb-16">
          <div className="md:col-span-7 relative overflow-hidden rounded-2xl border border-amber-900/30 aspect-video group">
            <Image src={images[0]} alt="Temple" fill className="object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0804]/80 via-[#0a0804]/20 to-transparent" />
            <div className="absolute bottom-4 left-5">
              <p className="text-amber-300 font-bold text-base sm:text-lg tracking-wide">18m Padi Karuppanasamy</p>
              <p className="text-amber-600/60 text-[10px] sm:text-xs tracking-widest">SUKKAMPATTI · KARUR · TAMIL NADU</p>
            </div>
            {/* Corner accent */}
            <div className="absolute top-3 right-3 w-8 h-8 border-t-2 border-r-2 border-amber-600/40 rounded-tr-lg" />
            <div className="absolute bottom-3 left-3 w-8 h-8 border-b-2 border-l-2 border-amber-600/40 rounded-bl-lg" />
          </div>
          <div className="md:col-span-5 flex flex-col md:grid md:grid-cols-1 gap-2 md:gap-3">
            {[images[1], images[2]].map((src, i) => (
              <div key={i} className="relative overflow-hidden rounded-2xl border border-amber-900/30 group aspect-video md:aspect-auto">
                <Image src={src} alt="Temple" fill className="object-cover opacity-35 group-hover:opacity-55 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0804]/60 to-transparent" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HISTORY ── */}
      <section className="py-28 border-t border-amber-900/20 relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-1/2 bg-gradient-to-l from-amber-950/10 to-transparent pointer-events-none" />

        <div className="container mx-auto px-6 lg:px-16 max-w-7xl relative z-10">
          {/* Section label */}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-px bg-amber-600" />
            <span className="text-amber-600 text-xs font-medium tracking-[0.25em] uppercase">{t.history.badge}</span>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 xl:gap-24 items-start">
            {/* Text column */}
            <div className="space-y-8">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-2">
                  {t.history.title}
                </h2>
                <p className="text-amber-500/70 text-sm tracking-widest uppercase">{t.history.subtitle}</p>
                <div className="flex items-center gap-2 mt-4">
                  <div className="h-1 w-12 bg-amber-600 rounded-full" />
                  <div className="h-1 w-4 bg-amber-800 rounded-full" />
                  <div className="h-1 w-2 bg-amber-900 rounded-full" />
                </div>
              </div>

              <div className="space-y-5">
                {[t.history.body1, t.history.body2].map((p, i) => (
                  <p key={i} className="text-neutral-400 leading-relaxed text-[15px] break-words whitespace-normal">{p}</p>
                ))}
                <div className="relative pl-5 border-l border-amber-800/50">
                  <div className="absolute left-[-5px] top-0 w-2.5 h-2.5 rounded-full bg-amber-700 border-2 border-[#0a0804]" />
                  <p className="text-neutral-500 leading-relaxed text-sm italic">{t.history.body3}</p>
                </div>
              </div>

              {/* Trustee card */}
              <div className="flex items-center gap-4 p-5 rounded-2xl border border-amber-900/30 bg-gradient-to-r from-amber-950/30 to-transparent">
                <div className="w-14 h-14 rounded-2xl bg-amber-900/30 border border-amber-800/40 flex items-center justify-center shrink-0 text-amber-400">
                  <User className="h-6 w-6" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-amber-600 uppercase tracking-[0.25em] mb-1">{t.contact.trustee}</p>
                  <p className="text-amber-100 font-bold text-lg leading-tight">{t.contact.name}</p>
                  <p className="text-neutral-500 text-xs mt-1">{t.contact.father} · {t.contact.mother}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-amber-700 ml-auto shrink-0" />
              </div>
            </div>

            {/* Milestones column */}
            <div className="grid grid-cols-2 gap-4">
              {t.history.milestones.map((m, i) => (
                <div
                  key={i}
                  className="group relative p-6 sm:p-8 rounded-3xl border border-neutral-800 bg-gradient-to-br from-neutral-900/80 to-neutral-950 hover:border-amber-700/50 hover:from-amber-950/40 transition-all duration-500 hover:-translate-y-1 overflow-hidden text-center"
                >
                  {/* Glow bg */}
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-600/0 to-amber-600/0 group-hover:from-amber-600/5 group-hover:to-transparent transition-all duration-500 rounded-3xl" />
                  <div className="relative z-10 space-y-3">
                    <div className="flex justify-center">
                      <div className="w-10 h-10 rounded-2xl bg-amber-950/60 border border-amber-900/50 flex items-center justify-center text-amber-400 group-hover:scale-110 transition-transform duration-300">
                        {emojiToIcon[m.icon]}
                      </div>
                    </div>
                    <p className="text-3xl sm:text-4xl font-bold bg-gradient-to-br from-amber-300 to-orange-500 bg-clip-text text-transparent">{m.year}</p>
                    <p className="text-[10px] sm:text-[11px] text-neutral-500 uppercase tracking-[0.2em] leading-snug">{m.label}</p>
                  </div>
                </div>
              ))}

              {/* Opening times card -  spans full width */}
              <div className="col-span-2 p-5 rounded-3xl border border-amber-900/30 bg-amber-950/20 flex items-center gap-5">
                <div className="w-12 h-12 rounded-2xl bg-amber-950/60 border border-amber-800/40 flex items-center justify-center text-amber-400 shrink-0">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-amber-400 font-semibold text-sm mb-1">
                    {language === "ta" ? "தினசரி திறப்பு நேரம்" : "Daily Opening Hours"}
                  </p>
                  <p className="text-neutral-500 text-xs">
                    {language === "ta" ? "காலை 4AM – 12PM · மாலை 4PM – 7PM · வாரம் 7 நாட்களும்" : "Morning 4AM – 12PM · Evening 4PM – 7PM · All 7 Days"}
                  </p>
                </div>
                <div className="ml-auto flex items-center gap-1.5 shrink-0">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-md shadow-emerald-500/60 animate-pulse" />
                  <span className="text-emerald-400 text-xs font-medium">{language === "ta" ? "திறந்திருக்கும்" : "Open"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── KARUPPASAMY ── */}
      <section className="py-28 border-t border-amber-900/20 relative overflow-hidden">
        <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-amber-700/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-br from-amber-950/15 via-transparent to-transparent pointer-events-none" />

        <div className="container mx-auto px-6 lg:px-16 max-w-7xl">
          {/* Centered header */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-600/60" />
              <Sword className="h-4 w-4 text-amber-500" />
              <span className="text-amber-600 text-xs font-medium tracking-[0.25em] uppercase">{t.karuppasamy.badge}</span>
              <Sword className="h-4 w-4 text-amber-500" />
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-600/60" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">{t.karuppasamy.title}</h2>
            <p className="text-neutral-500 text-sm mt-2">{t.karuppasamy.subtitle}</p>
          </div>

          <div className="grid lg:grid-cols-5 gap-12 items-start">
            {/* Body text */}
            <div className="lg:col-span-3 space-y-6">
              {[t.karuppasamy.body1, t.karuppasamy.body2, t.karuppasamy.body3].map((para, i) => (
                <div key={i} className="flex gap-4">
                  <div className="shrink-0 w-1 rounded-full bg-gradient-to-b from-amber-500 via-amber-700 to-amber-900/0 mt-2" />
                  <p className="text-neutral-400 leading-relaxed text-[15px] break-words whitespace-normal">{para}</p>
                </div>
              ))}

              {/* Height visualizer */}
              <div className="mt-10 p-6 rounded-3xl border border-neutral-800 bg-neutral-900/40">
                <p className="text-xs text-neutral-600 uppercase tracking-widest mb-5 text-center">
                  {language === "ta" ? "சிலை உயரம் ஒப்பீடு" : "Statue Height Comparison"}
                </p>
                <div className="flex items-end justify-center gap-2 h-32">
                  {[30, 45, 60, 75, 90].map((h, i) => (
                    <div
                      key={i}
                      className="w-8 rounded-t-lg bg-gradient-to-t from-amber-900/70 to-amber-800/30 border border-amber-900/40 transition-all hover:from-amber-800"
                      style={{ height: `${h}%` }}
                    />
                  ))}
                  <div className="w-8 rounded-t-xl bg-gradient-to-t from-amber-600 to-yellow-300 border border-amber-500 relative shadow-lg shadow-amber-600/30">
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-amber-400 font-bold text-sm whitespace-nowrap">22ft</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Deity cards */}
            <div className="lg:col-span-2 flex flex-wrap justify-center gap-3">
              {t.karuppasamy.deities.map((deity, i) => (
                <div
                  key={i}
                  className={`group relative p-4 sm:p-5 rounded-2xl border transition-all duration-300 hover:-translate-y-1 text-center overflow-hidden
                    ${i === 0
                      ? "w-full border-amber-700/50 bg-gradient-to-r from-amber-950/50 to-amber-900/20 shadow-lg shadow-amber-950/40"
                      : "w-[calc(50%-0.375rem)] min-w-[120px] border-neutral-800 bg-neutral-900/50 hover:border-amber-800/40 hover:bg-amber-950/20"
                    }`}
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-b from-amber-900/10 to-transparent rounded-2xl" />
                  <div className="relative z-10 space-y-2">
                    <div className="flex justify-center">
                      <div className={`flex items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110
                        ${i === 0 ? "w-10 h-10 sm:w-12 sm:h-12 bg-amber-900/50 border border-amber-700/50 text-amber-300" : "w-8 h-8 sm:w-10 sm:h-10 bg-neutral-800 border border-neutral-700 text-amber-400"}`}>
                        {emojiToIcon[deity.icon]}
                      </div>
                    </div>
                    <p className={`font-bold text-xs sm:text-sm ${i === 0 ? "text-amber-200 text-base" : "text-neutral-200"}`}>{deity.name}</p>
                    <p className="text-neutral-600 text-[10px] sm:text-xs">{deity.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── IMPORTANCE ── */}
      <section className="py-28 border-t border-amber-900/20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(120,53,15,0.08),_transparent_70%)] pointer-events-none" />

        <div className="container mx-auto px-6 lg:px-16 max-w-7xl">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-600/60" />
              <Sparkles className="h-4 w-4 text-amber-500" />
              <span className="text-amber-600 text-xs font-medium tracking-[0.25em] uppercase">{t.importance.badge}</span>
              <Sparkles className="h-4 w-4 text-amber-500" />
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-600/60" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white">{t.importance.title}</h2>
            <p className="text-neutral-500 text-sm mt-2">{t.importance.subtitle}</p>
          </div>

          <div className="flex flex-col md:grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {t.importance.points.map((point, i) => (
              <div
                key={i}
                className="group relative p-7 rounded-3xl border border-neutral-800/80 bg-neutral-900/30 hover:border-amber-800/50 hover:bg-gradient-to-br hover:from-amber-950/30 hover:to-neutral-900 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-amber-950/30 overflow-hidden"
              >
                {/* Number watermark */}
                <span className="absolute top-4 right-5 text-6xl font-bold text-neutral-800/50 group-hover:text-amber-900/30 transition-colors leading-none select-none">
                  {String(i + 1).padStart(2, '0')}
                </span>

                <div className="relative z-10 space-y-4">
                  <div className="w-12 h-12 rounded-2xl bg-amber-950/50 border border-amber-900/40 flex items-center justify-center text-amber-400 group-hover:scale-110 group-hover:border-amber-700/60 transition-all duration-300">
                    {emojiToIcon[point.icon]}
                  </div>
                  <h3 className="font-bold text-neutral-100 text-lg leading-snug group-hover:text-amber-300 transition-colors duration-300">
                    {point.title}
                  </h3>
                  <div className="h-px bg-neutral-800 group-hover:bg-amber-900/50 transition-colors" />
                  <p className="text-neutral-500 text-[14px] leading-relaxed break-words whitespace-normal">{point.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRADITIONS ── */}
      <section className="py-28 border-t border-amber-900/20 relative overflow-hidden">
        <div className="absolute right-0 top-1/4 w-96 h-96 bg-orange-950/15 rounded-full blur-[120px] pointer-events-none" />

        <div className="container mx-auto px-6 lg:px-16 max-w-5xl">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-600/60" />
              <Sprout className="h-4 w-4 text-amber-500" />
              <span className="text-amber-600 text-xs font-medium tracking-[0.25em] uppercase">{t.traditions.badge}</span>
              <Sprout className="h-4 w-4 text-amber-500" />
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-600/60" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white">{t.traditions.title}</h2>
            <p className="text-neutral-500 text-sm mt-2">{t.traditions.subtitle}</p>
          </div>

          <div className="space-y-3">
            {t.traditions.items.map((item, i) => {
              const isOpen = expandedTradition === i;
              return (
                <button
                  key={i}
                  onClick={() => setExpandedTradition(isOpen ? null : i)}
                  className="w-full text-left group"
                >
                  <div className={`relative rounded-2xl border transition-all duration-300 overflow-hidden
                    ${isOpen
                      ? "bg-gradient-to-r from-amber-950/50 to-amber-900/20 border-amber-700/50 shadow-xl shadow-amber-950/30"
                      : "bg-neutral-900/40 border-neutral-800 hover:border-neutral-700 hover:bg-neutral-900/60"
                    }`}
                  >
                    {/* Accent line left */}
                    <div className={`absolute left-0 top-0 bottom-0 w-[3px] transition-all duration-300 rounded-l-2xl
                      ${isOpen ? "bg-gradient-to-b from-amber-500 to-amber-700" : "bg-neutral-800 group-hover:bg-neutral-700"}`} />

                    <div className="pl-6 pr-5 py-5">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 shrink-0
                            ${isOpen ? "bg-amber-900/50 border border-amber-700/50 text-amber-300" : "bg-neutral-800 border border-neutral-700 text-amber-500 group-hover:border-neutral-600"}`}>
                            {emojiToIcon[item.icon]}
                          </div>
                          <h3 className={`font-bold text-base transition-colors duration-300 ${isOpen ? "text-amber-200" : "text-neutral-200"}`}>
                            {item.title}
                          </h3>
                        </div>
                        <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center border transition-all duration-300
                          ${isOpen ? "bg-amber-900/50 border-amber-700/50 text-amber-300 rotate-180" : "bg-neutral-800 border-neutral-700 text-neutral-500"}`}>
                          <ChevronDown className="h-4 w-4" />
                        </div>
                      </div>

                      {isOpen && (
                        <div className="mt-4 ml-4 sm:ml-14 pr-2">
                          <p className="text-neutral-400 text-sm leading-relaxed">{item.desc}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── ARCHITECTURE ── */}
      <section className="py-20 md:py-28 border-t border-amber-900/20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(120,53,15,0.1),_transparent_60%)] pointer-events-none" />

        <div className="container mx-auto px-4 lg:px-16 max-w-7xl overflow-x-hidden">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-600/60" />
              <Landmark className="h-4 w-4 text-amber-500" />
              <span className="text-amber-600 text-xs font-medium tracking-[0.25em] uppercase">{t.architecture.badge}</span>
              <Landmark className="h-4 w-4 text-amber-500" />
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-600/60" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white">{t.architecture.title}</h2>
            <p className="text-neutral-500 text-sm mt-2">{t.architecture.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* Left: text + spec table */}
            <div className="space-y-6 order-2 lg:order-1 min-w-0 w-full max-w-full overflow-hidden">
              {[t.architecture.body1, t.architecture.body2, t.architecture.body3].map((para, i) => (
                <p key={i} className="text-neutral-400 leading-relaxed text-[15px] break-words whitespace-normal">{para}</p>
              ))}

              {/* Feature spec table */}
              <div className="mt-6 rounded-3xl border border-neutral-800 overflow-hidden divide-y divide-neutral-800/80">
                {t.architecture.features.map((f, i) => (
                  <div key={i} className={`flex items-center justify-between px-4 py-4 transition-colors hover:bg-amber-950/15 min-w-0 ${i % 2 === 0 ? "bg-neutral-900/50" : "bg-neutral-900/20"}`}>
                    <div className="flex items-center gap-3 text-neutral-500 text-sm">
                      {f.icon}
                      <span>{f.label}</span>
                    </div>
                    <span className="text-amber-400 font-semibold text-sm">{f.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: images */}
            <div className="order-1 lg:order-2 space-y-3 min-w-0 w-full max-w-full overflow-hidden">
              <div className="relative h-72 rounded-3xl overflow-hidden border border-amber-900/30 group">
                <Image src={images[0]} alt="Temple Architecture" fill className="object-cover opacity-45 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0804]/80 to-transparent" />
                {/* Decorative frame corners */}
                <div className="absolute top-4 left-4 w-10 h-10 border-t-2 border-l-2 border-amber-600/50 rounded-tl-lg" />
                <div className="absolute top-4 right-4 w-10 h-10 border-t-2 border-r-2 border-amber-600/50 rounded-tr-lg" />
                <div className="absolute bottom-4 left-4 w-10 h-10 border-b-2 border-l-2 border-amber-600/50 rounded-bl-lg" />
                <div className="absolute bottom-4 right-4 w-10 h-10 border-b-2 border-r-2 border-amber-600/50 rounded-br-lg" />
                <div className="absolute bottom-5 left-5 right-5">
                  <p className="text-amber-300 font-bold text-lg">18m Padi Karuppanasamy</p>
                  <p className="text-amber-700/60 text-xs tracking-widest uppercase">
                    {language === "ta" ? "சுக்கம்பட்டி, கரூர்" : "Sukkampatti, Karur"}
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:grid md:grid-cols-2 gap-3 w-full">
                {[images[1], images[2]].map((src, i) => (
                  <div key={i} className="relative aspect-video rounded-2xl overflow-hidden border border-neutral-800 group">
                    <Image src={src} alt="Temple" fill className="object-cover opacity-35 group-hover:opacity-55 group-hover:scale-105 transition-all duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0804]/60 to-transparent" />
                  </div>
                ))}
              </div>

              {/* Deity scroll strip - wrapped for mobile */}
              <div className="flex flex-wrap justify-center gap-2 pt-2 w-full">
                {t.karuppasamy.deities.map((d, i) => (
                  <div key={i} className="flex flex-col items-center gap-1.5 bg-neutral-900/70 border border-neutral-800 rounded-2xl px-2 py-3 hover:border-amber-800/50 hover:bg-amber-950/20 transition-colors min-w-0 flex-shrink-0" style={{ width: 'calc(33.333% - 0.5rem)' }}>
                    <div className="text-amber-400">
                      {emojiToIcon[d.icon]}
                    </div>
                    <span className="text-[10px] text-neutral-500 font-medium text-center leading-tight break-words">{d.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section className="py-20 border-t border-amber-900/20 relative overflow-hidden">
        {/* Top glow bar */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-700/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-amber-950/15 via-transparent to-transparent pointer-events-none" />

        <div className="container mx-auto px-6 lg:px-16 max-w-5xl relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-600/60" />
              <MapPin className="h-4 w-4 text-amber-500" />
              <span className="text-amber-600 text-xs font-medium tracking-[0.25em] uppercase">
                {language === "ta" ? "தொடர்பு கொள்ள" : "Get in Touch"}
              </span>
              <MapPin className="h-4 w-4 text-amber-500" />
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-600/60" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              {language === "ta" ? "தொடர்பு தகவல்" : "Contact & Location"}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Trustee info card */}
            <div className="p-7 rounded-3xl border border-amber-900/40 bg-gradient-to-br from-amber-950/30 to-neutral-900/50 space-y-4">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-amber-900/40 border border-amber-800/40 flex items-center justify-center text-amber-400">
                  <User className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[10px] text-amber-600 uppercase tracking-[0.25em]">{t.contact.trustee}</p>
                  <p className="text-amber-100 font-bold text-xl">{t.contact.name}</p>
                </div>
              </div>

              <Separator className="bg-amber-900/30" />

              {[t.contact.father, t.contact.mother, t.contact.brother].map((info, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Check className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                  <p className="text-neutral-400 text-sm">{info}</p>
                </div>
              ))}

              <Separator className="bg-amber-900/30" />

              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-neutral-500 text-sm leading-relaxed">{t.contact.address}</p>
              </div>
            </div>

            {/* Phone + hours */}
            <div className="space-y-4">
              {t.contact.phones.map((num, i) => (
                <a key={i} href={`tel:${num}`} className="block group">
                  <div className="flex items-center justify-between p-5 rounded-2xl border border-neutral-800 bg-neutral-900/50 hover:border-amber-700/50 hover:bg-amber-950/25 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-amber-950/30">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-amber-950/60 border border-amber-900/40 flex items-center justify-center text-amber-400 group-hover:scale-110 transition-transform">
                        <Phone className="h-4 w-4" />
                      </div>
                      <span className="text-amber-300 font-mono tracking-wider text-lg">+91 {num}</span>
                    </div>
                    <ArrowRight className="h-4 w-4 text-neutral-600 group-hover:text-amber-500 group-hover:translate-x-1 transition-all" />
                  </div>
                </a>
              ))}

              {/* Hours card */}
              <div className="p-5 rounded-2xl border border-emerald-900/40 bg-emerald-950/20 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-md shadow-emerald-500/50 animate-pulse" />
                  <span className="text-emerald-400 text-sm font-semibold">
                    {language === "ta" ? "இன்று திறந்திருக்கும்" : "Open Today & Every Day"}
                  </span>
                </div>
                <div className="space-y-2">
                  {[
                    { label: language === "ta" ? "காலை" : "Morning", time: "4:00 AM – 12:00 PM" },
                    { label: language === "ta" ? "மாலை" : "Evening", time: "4:00 PM – 7:00 PM" },
                  ].map((slot, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-neutral-500 text-sm">{slot.label}</span>
                      <span className="text-neutral-300 text-sm font-mono">{slot.time}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Direction hint */}
              <div className="flex items-center gap-3 p-4 rounded-2xl border border-neutral-800 bg-neutral-900/30">
                <Zap className="h-4 w-4 text-amber-600 shrink-0" />
                <p className="text-neutral-600 text-xs leading-relaxed">
                  {language === "ta"
                    ? "கடவூர் தாலுகா, கணியாலம்பட்டி ரோட்டில் சுக்கம்பட்டி அருகே உள்ளது"
                    : "Located near Sukkampatti on Kaniyalampatti Road, Kadavoor Thaluk"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer line */}
      <div className="border-t border-amber-900/20 py-6 text-center">
        <div className="flex items-center justify-center gap-3">
          <div className="h-px w-8 bg-amber-900/40" />
          <Sparkles className="h-3.5 w-3.5 text-amber-800" />
          <p className="text-neutral-700 text-xs tracking-[0.3em] uppercase">18m Padi Karuppanasamy Temple</p>
          <Sparkles className="h-3.5 w-3.5 text-amber-800" />
          <div className="h-px w-8 bg-amber-900/40" />
        </div>
      </div>
    </main>
  );
}